import {
  Icon16AlertTriangle,
  Icon16Checkmark,
  Icon16Dash,
  Typography,
} from "@cur8/maneki";
import { Measurement } from "@cur8/rich-entity";
import { MetricGroup } from "./measurementConfig";
import { getEffectiveConversion } from "./measurementConversion";
import {
  formatMeasurementValue,
  formatUnitPlaceholder,
} from "./measurementFormat";
import styles from "./styles.module.sass";

export type GroupTileStatus =
  // Expected rather than wrong (a skipped group). Reads teal, and the message
  // stands in for the readings there aren't any of.
  | { kind: "note"; text: string }
  // Readings are there but suspect. Reads amber. `difference` reports the pair
  // comparison, `implausibleMetricNames` marks individual readings, and either
  // can be present without the other.
  | {
      kind: "caution";
      title: string;
      difference?: {
        label: string;
        amount: number;
        limit: number;
        limitIsInclusive: boolean;
      };
      implausibleMetricNames: string[];
    };

interface GroupTileProps {
  group: MetricGroup;
  existingByMetric: Map<string, Measurement>;
  countryCode: string | undefined;
  unitOverrides: Map<string, boolean>;
  status?: GroupTileStatus;
  onClick: () => void;
}

export function GroupTile({
  group,
  existingByMetric,
  countryCode,
  unitOverrides,
  status,
  onClick,
}: GroupTileProps) {
  const doneCount = group.metrics.filter((m) =>
    existingByMetric.has(m.metricName)
  ).length;
  const isDone = status == null && doneCount === group.metrics.length;

  const readings = group.metrics.map((metric) => {
    const convDef = getEffectiveConversion(
      metric,
      countryCode,
      unitOverrides.get(metric.metricName)
    );
    return {
      metricName: metric.metricName,
      label: metric.variant ? metric.variant.value : metric.label,
      value:
        formatMeasurementValue(
          metric,
          existingByMetric.get(metric.metricName),
          convDef
        ) ?? formatUnitPlaceholder(metric, convDef),
    };
  });

  return (
    <button
      type="button"
      className={styles.tile}
      data-done={isDone || undefined}
      data-caution={status?.kind === "caution" || undefined}
      data-note={status?.kind === "note" || undefined}
      onClick={onClick}
    >
      {status?.kind === "caution" ? (
        <>
          <div className={styles.tileHeader}>
            <span className={styles.cautionEyebrow}>{status.title}</span>
            <span className={styles.tileAlert}>
              <Icon16AlertTriangle />
            </span>
          </div>
          <span className={styles.cautionTileLabel}>{group.label}</span>
          <div className={styles.groupValues}>
            {readings.map((reading) => (
              <div key={reading.metricName} className={styles.readingRow}>
                <span>{reading.label}:</span>
                <span
                  className={styles.readingValue}
                  data-implausible={
                    status.implausibleMetricNames.includes(
                      reading.metricName
                    ) || undefined
                  }
                >
                  {reading.value}
                </span>
              </div>
            ))}
          </div>
          {status.difference && (
            <>
              {/* Inside the conditional, so a reading-only caution leaves no
                  divider dangling. */}
              <div className={styles.tileDivider} />
              <div className={styles.differenceFooter}>
                <span className={styles.differenceValue}>
                  {status.difference.label}:{" "}
                  <span className={styles.differenceAmount}>
                    {status.difference.amount} mmHg
                  </span>
                </span>
                <span>
                  Limit: {status.difference.limitIsInclusive ? "≥" : ">"}
                  {status.difference.limit} mmHg
                </span>
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <div className={styles.tileHeader}>
            <Typography variant="label-s" color={status ? "info" : undefined}>
              {group.label}
            </Typography>
            {isDone && (
              <span className={styles.tileCheck}>
                <Icon16Checkmark />
              </span>
            )}
            {status && (
              <span className={styles.tileNote}>
                <Icon16Dash />
              </span>
            )}
          </div>
          {status ? (
            <Typography variant="body-s" color="info">
              {status.text}
            </Typography>
          ) : (
            <div className={styles.groupValues}>
              {readings.map((reading) => (
                <Typography
                  key={reading.metricName}
                  variant="body-s"
                  color={isDone ? "inverted" : "subtle"}
                >
                  {reading.label}: {reading.value}
                </Typography>
              ))}
            </div>
          )}
        </>
      )}
    </button>
  );
}
